import {
  FavoritesFragment,
  GetFavoritesQuery,
  UpdateFavoriteListMutation,
  FavoriteListItemFragment,
} from '../graphql/product/generated/graphql-types'
import { createProductList } from '../utils/create-product-list'

export type FavoriteProductList = NonNullable<FavoritesFragment['productLists']>

function upsertExistingFavoriteList({
  productId,
  quantity,
  productListItemId,
  productListId,
  existingFavoriteList,
  allProductLists,
}: {
  productId: string
  quantity: number
  productListItemId: number | undefined
  productListId: number | undefined
  existingFavoriteList: NonNullable<FavoriteProductList[0]>
  allProductLists: FavoriteProductList
}) {
  let updatedProductList

  if (quantity > 0) {
    // Update existing item
    const existingItem = existingFavoriteList?.items.find(item => item.id === productListItemId)

    if (existingItem) {
      // Update item in list
      updatedProductList = updateItemInList({
        allProductLists,
        existingFavoriteList,
        existingItem,
        productListId,
        productListItemId,
        quantity,
      })

      // Add item to list
    } else {
      updatedProductList = addItemToList({
        allProductLists,
        existingFavoriteList,
        productId,
        productListId,
        productListItemId,
      })
    }
    // Delete item from list
  } else {
    deleteItemFromList({
      allProductLists,
      existingFavoriteList,
      productId,
      productListId,
    })
  }

  return updatedProductList
}

function addItemToList({
  existingFavoriteList,
  productListItemId,
  productId,
  allProductLists,
  productListId,
}: {
  existingFavoriteList: NonNullable<FavoriteProductList[0]>
  productListItemId: number | undefined
  productId: string
  allProductLists: FavoriteProductList
  productListId: number | undefined
}) {
  const updatedFavoriteListItems = [
    ...existingFavoriteList.items,
    {
      comment: null,
      id: productListItemId,
      productErpIdentifier: productId,
    },
  ]

  const updatedFavoriteList = {
    ...existingFavoriteList,
    items: updatedFavoriteListItems,
  }

  const updatedProductList = [
    ...allProductLists.filter(i => i?.id !== productListId),
    updatedFavoriteList,
  ]

  return updatedProductList
}

function updateItemInList({
  existingItem,
  existingFavoriteList,
  productListItemId,
  allProductLists,
  quantity,
  productListId,
}: {
  existingItem: NonNullable<FavoriteProductList[0]>['items'][0]
  existingFavoriteList: NonNullable<FavoriteProductList[0]>
  productListItemId: number | undefined
  allProductLists: FavoriteProductList
  quantity: number
  productListId: number | undefined
}) {
  const updatedItem = { ...existingItem, quantity }

  const updatedFavoriteListItems = [
    ...existingFavoriteList.items.filter(item => item.id !== productListItemId),
    updatedItem,
  ].sort((a, b) => a.id - b.id)

  const updatedFavoriteList = {
    ...existingFavoriteList,
    items: updatedFavoriteListItems,
  }

  const updatedProductList = [
    ...allProductLists.filter(i => i?.id !== productListId),
    updatedFavoriteList,
  ]

  return updatedProductList
}

function deleteItemFromList({
  existingFavoriteList,
  allProductLists,
  productListId,
  productId,
}: {
  existingFavoriteList: NonNullable<FavoriteProductList[0]>
  allProductLists: FavoriteProductList
  productListId: number | undefined
  productId: string
}) {
  const updatedFavoriteListItems = existingFavoriteList.items.filter(
    item => item.productErpIdentifier !== productId
  )
  const updatedFavoriteList: FavoriteListItemFragment = {
    ...existingFavoriteList,
    items: updatedFavoriteListItems,
  }
  const updatedProductList = [
    ...allProductLists.filter(i => i?.id !== productListId),
    updatedFavoriteList,
  ]

  return updatedProductList
}

export function getUpdatedFavoritesCache({
  displayName,
  favoriteListName,
  favoritesData,
  newData,
  productId,
  quantity,
}: {
  displayName: string
  favoriteListName: string
  favoritesData: GetFavoritesQuery
  newData: UpdateFavoriteListMutation
  productId: string
  quantity: number
}) {
  const allProductLists = favoritesData?.favorites?.productLists ?? []

  const productListId = newData?.updateFavoriteListWithProduct?.productListId
  const productListItemId = newData?.updateFavoriteListWithProduct?.productListItemId

  let updatedProductList

  const existingFavoriteList = allProductLists.find(listItem => listItem?.id === productListId)

  if (existingFavoriteList) {
    updatedProductList = upsertExistingFavoriteList({
      allProductLists,
      existingFavoriteList,
      productId,
      productListId,
      productListItemId,
      quantity,
    })
  } else {
    // Create new list and add item
    updatedProductList = [
      ...allProductLists,
      createProductList(productListId, favoriteListName, productListItemId, productId, displayName),
    ]
  }

  return updatedProductList
}
